import React from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Typography } from '@mui/material';

import SurchargeDates from '~/components/Vendors/Properties/SurchargeDates';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { roomRateAvailabilityQueryKeys } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useInvalidateAllRoomRateAvailability } from '~/hooks/apis/reservation/useInvalidateAllRoomRateAvailability';
import { useGetRoomRateInternalSurchargeDates } from '~/hooks/apis/reservation/useGetRoomRateInternalSurchargeDates';
import { useGetSurchargeDateBlockSchema } from '~/hooks/apis/reservation/useGetSurchargeDateBlockSchema';

import ReservationService from '~/services/ReservationService';

import presentFXRates from '~/utils/presentFXRates';

const SURCHARGE_TYPE = 'internal';

interface Props {
  propertyId: string;
  vendorId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
  propertyRoomTypes: Array<App.RoomType>;
  vendorCurrencyCode: string;
}

export default function RevenueManagementTab({
  propertyId,
  roomType,
  roomRate,
  propertyRoomTypes,
  vendorCurrencyCode,
}: Props) {
  const queryClient = useQueryClient();
  const { data: surchargeDates = [], refetch } = useGetRoomRateInternalSurchargeDates(
    propertyId,
    roomType.id,
    roomRate.id,
  );
  const { data: surchargeSchema } = useGetSurchargeDateBlockSchema(propertyId, roomType.id, roomRate.id);
  const { data: fxRates } = useGetFxRates();
  const invalidateAll = useInvalidateAllRoomRateAvailability();

  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: roomRateAvailabilityQueryKeys.internalSurcharge(propertyId, roomType.id, roomRate.id),
    });
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Revenue management sell prices by date range for this room rate.
      </Typography>

      <SurchargeDates
        propertyId={propertyId}
        roomType={roomType}
        roomRate={roomRate}
        propertyRoomTypes={propertyRoomTypes}
        vendorCurrencyCode={vendorCurrencyCode}
        surchargeDates={surchargeDates}
        surchargeType={SURCHARGE_TYPE}
        deleteSurchargeDateBlock={async (id: string) => {
          await ReservationService.deleteSurchargeDateBlock(id, propertyId, roomType.id, roomRate.id);
          refetch();
        }}
        deleteSurchargeDateBlockByRoomRateId={async () => {
          await ReservationService.deleteSurchargeDateBlockByRoomRateId(propertyId, roomRate.id, SURCHARGE_TYPE);
          invalidate();
        }}
        deleteSurchargeDateBlockByRatePlanId={async (ratePlanId: string) => {
          await ReservationService.deleteSurchargeDateBlockByRatePlanId(ratePlanId, SURCHARGE_TYPE);
          invalidateAll();
        }}
        onUpdateSurchargeDates={refetch}
        surchargeSchema={surchargeSchema}
        foreignExchangeRates={fxRates ? presentFXRates(fxRates) : {}}
      />
    </>
  );
}
